import React from 'react';
import { Box, Button, Typography, TextField, Alert, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import { useChartData } from './ChartDataProvider';

const MotifPlacementPanel = () => {
  const { motifForPlacement, motifPlacementPosition, motifColorSource, rows, cols, actions } = useChartData();

  if (!motifForPlacement) return null;

  // Get motif dimensions
  const motifData = motifForPlacement.data || motifForPlacement.colordata || [];
  const motifRows = motifData.length;
  const motifCols = motifData[0] ? motifData[0].length : 0;

  // Calculate required chart dimensions
  const requiredRows = Math.max(rows, motifPlacementPosition.row + motifRows);
  const requiredCols = Math.max(cols, motifPlacementPosition.col + motifCols);
  const willExtend = requiredRows > rows || requiredCols > cols;

  const handleRowChange = (e) => {
    const newRow = Math.max(0, parseInt(e.target.value) || 0);
    actions.setMotifPlacementPosition({ ...motifPlacementPosition, row: newRow });
  };

  const handleColChange = (e) => {
    const newCol = Math.max(0, parseInt(e.target.value) || 0);
    actions.setMotifPlacementPosition({ ...motifPlacementPosition, col: newCol });
  };

  const handleConfirm = () => {
    actions.importMotif(motifForPlacement, motifPlacementPosition);
  };

  const handleCancel = () => {
    actions.setMotifForPlacement(null);
  };

  const handleColorSourceChange = (e) => {
    actions.setMotifColorSource(e.target.value);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
        Place Motif
      </Typography>
      
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {motifForPlacement.name}
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Size: {motifRows} × {motifCols}
      </Typography>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Position:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            label="Row"
            type="number"
            value={motifPlacementPosition.row}
            onChange={handleRowChange}
            size="small"
            inputProps={{ min: 0, step: 1 }}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Col"
            type="number"
            value={motifPlacementPosition.col}
            onChange={handleColChange}
            size="small"
            inputProps={{ min: 0, step: 1 }}
            sx={{ flex: 1 }}
          />
        </Box>
      </Box>
      
      {/* Color Source Selection */}
      <Box sx={{ mb: 2 }}>
        <FormControl component="fieldset">
          <FormLabel component="legend" sx={{ mb: 1, fontSize: '0.875rem' }}>
            Color Palette:
          </FormLabel>
          <RadioGroup
            value={motifColorSource}
            onChange={handleColorSourceChange}
            row
          >
            <FormControlLabel 
              value="current" 
              control={<Radio size="small" />} 
              label="Chart Colors" 
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
            <FormControlLabel 
              value="motif" 
              control={<Radio size="small" />} 
              label="Motif Colors" 
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          </RadioGroup>
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
            {motifColorSource === 'current' 
              ? 'Uses your chart\'s current color palette'
              : 'Imports and uses the motif\'s original colors'
            }
          </Typography>
        </FormControl>
      </Box>
      
      {willExtend && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Chart will be extended to {requiredRows} × {requiredCols}
        </Alert>
      )}
      
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
        Drag the blue rectangle on the chart or use the position fields above
      </Typography>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          startIcon={<Check />}
          onClick={handleConfirm}
          size="small"
          fullWidth
        >
          Import
        </Button>
        <Button
          variant="outlined"
          startIcon={<Close />}
          onClick={handleCancel}
          size="small"
          fullWidth
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default MotifPlacementPanel;
